import React from 'react';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { format, parseISO } from 'date-fns';
import { Risk } from '../../types/risk.types';
import { severityBgClass, severityLabel } from '../../lib/severity';
import { LedgerSkeleton } from '../shell/LedgerSkeleton';
import { cn } from '../../lib/cn';

// Display math only ("N more entries", "Page X / Y"). The server default page size is 20
// but DB-configurable — if that setting changes, this display drifts. Prev/Next bounds do
// NOT drift: they come from the DRF envelope via hasNext/hasPrevious.
const PAGE_SIZE = 20;

export interface RegisterTableProps {
  rows: Risk[];
  count: number;
  page: number;
  isPending: boolean;
  ordering: string;
  selectedId: string | null;
  hasNext: boolean;
  hasPrevious: boolean;
  onSelect: (id: string) => void;
  onOrderingChange: (ordering: string) => void;
  onPageChange: (page: number) => void;
  onResetView: () => void;
}

const shortRef = (ref: string) => ref.split('-').pop() ?? ref;

const ownerName = (risk: Risk) => risk.owner_full_name || risk.owner_email || '—';

const reviewCell = (risk: Risk) => {
  if (!risk.next_review_date) return <span className="text-ink-muted">—</span>;
  const overdue = risk.next_review_date <= format(new Date(), 'yyyy-MM-dd');
  return (
    <span className={overdue ? 'text-sev-critical' : 'text-ink-muted'}>
      {format(parseISO(risk.next_review_date), 'MMM d, yyyy').toUpperCase()}
    </span>
  );
};

// Sortable column id → DRF ordering field.
const SORT_FIELD: Record<string, string> = {
  reference_number: 'reference_number',
  severity: 'residual_score', // severity is a computed view of the residual score
  residual_score: 'residual_score',
  next_review_date: 'next_review_date',
};

const columns: ColumnDef<Risk>[] = [
  {
    id: 'reference_number',
    header: 'Nº',
    cell: ({ row }) => (
      <span className="font-mono text-scale-0 text-ink-muted" title={row.original.reference_number}>
        {shortRef(row.original.reference_number)}
      </span>
    ),
  },
  {
    id: 'risk',
    header: 'Risk',
    cell: ({ row }) => (
      <span className="text-ink">
        <span className="font-medium">{row.original.title}</span>
        {row.original.vendor_note && (
          <span className="font-serif italic text-scale-0 text-ink-muted"> — vendor: {row.original.vendor_note}</span>
        )}
      </span>
    ),
  },
  {
    id: 'severity',
    header: 'Severity',
    cell: ({ row }) => (
      <span className="inline-flex items-center">
        <span aria-hidden className={cn('mr-1.5 h-2 w-2', severityBgClass(row.original.residual_score ?? 0))} />
        {severityLabel(row.original.residual_score ?? 0)}
      </span>
    ),
  },
  {
    id: 'status',
    header: 'Status',
    cell: ({ row }) => <span className="cap-label">{row.original.status}</span>,
  },
  {
    id: 'owner',
    header: 'Owner',
    cell: ({ row }) => (
      <span className="text-ink-muted">{ownerName(row.original)}</span>
    ),
  },
  {
    id: 'residual_score',
    header: 'Res.',
    cell: ({ row }) => <span className="font-mono">{row.original.residual_score}</span>,
  },
  {
    id: 'next_review_date',
    header: 'Review',
    cell: ({ row }) => <span className="font-mono">{reviewCell(row.original)}</span>,
  },
];

export const RegisterTable: React.FC<RegisterTableProps> = (props) => {
  const { rows, count, page, isPending, ordering, selectedId, hasNext, hasPrevious } = props;
  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    manualPagination: true,
  });

  if (isPending) return <LedgerSkeleton lines={8} className="mt-4" />;

  if (rows.length === 0) {
    return (
      <p className="py-6 font-serif text-scale-1 italic text-ink-muted">
        Nothing on file for this view.{' '}
        <button type="button" onClick={props.onResetView} className="cursor-pointer underline hover:text-accent">
          Show all risks
        </button>
      </p>
    );
  }

  const toggleOrdering = (field: string) => {
    props.onOrderingChange(ordering === `-${field}` ? field : `-${field}`);
  };
  const sortIndicator = (field: string) =>
    ordering === `-${field}` ? ' ▾' : ordering === field ? ' ▴' : '';
  const ariaSort = (field: string): 'ascending' | 'descending' | 'none' =>
    ordering === field ? 'ascending' : ordering === `-${field}` ? 'descending' : 'none';

  const pageCount = Math.max(1, Math.ceil(count / PAGE_SIZE));
  const remaining = Math.max(0, count - page * PAGE_SIZE);

  return (
    <div>
      <table className="hidden w-full border-collapse md:table" aria-label="Risk register">
        <thead>
          <tr className="border-b border-ink">
            {table.getFlatHeaders().map((header) => {
              const field = SORT_FIELD[header.column.id];
              const label = flexRender(header.column.columnDef.header, header.getContext());
              // Severity and Residual share one sort field; only the field's own
              // column carries aria-sort so the table never reports two sorted
              // columns at once.
              const sortState = field
                ? header.column.id === field
                  ? ariaSort(field)
                  : 'none'
                : undefined;
              return (
                <th
                  key={header.id}
                  aria-sort={sortState}
                  className={cn(
                    'cap-label py-1 text-left font-normal',
                    ['residual_score', 'next_review_date'].includes(header.column.id) && 'text-right',
                  )}
                >
                  {field ? (
                    <button type="button" onClick={() => toggleOrdering(field)} className="cap-label cursor-pointer hover:!text-ink">
                      {label}
                      {sortIndicator(field)}
                    </button>
                  ) : (
                    label
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr
              key={row.original.id}
              onClick={() => props.onSelect(row.original.id)}
              aria-current={row.original.id === selectedId ? 'true' : undefined}
              className={cn(
                'cursor-pointer border-b border-rule-faint text-scale-1 hover:bg-accent-soft',
                row.original.id === selectedId && 'bg-accent-soft shadow-[inset_2px_0_0_var(--color-accent)]',
              )}
            >
              {row.getVisibleCells().map((cell) => (
                <td
                  key={cell.id}
                  className={cn(
                    'py-1.5 pr-3',
                    ['residual_score', 'next_review_date'].includes(cell.column.id) && 'text-right',
                  )}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      {/* Mobile: rows reflow to two-line entries */}
      <div className="md:hidden">
        {rows.map((risk) => (
          <button
            key={risk.id}
            type="button"
            onClick={() => props.onSelect(risk.id)}
            className="block w-full border-b border-rule-faint py-2.5 text-left"
          >
            <span className="flex items-center text-scale-1 font-semibold text-ink">
              <span aria-hidden className={cn('mr-1.5 h-2 w-2 shrink-0', severityBgClass(risk.residual_score ?? 0))} />
              {risk.title}
            </span>
            <span className="mt-0.5 flex justify-between text-scale-0 text-ink-muted">
              <span className="font-mono">
                {shortRef(risk.reference_number)} · {risk.status} · {ownerName(risk)}
              </span>
              {risk.next_review_date ? (
                <span className="font-mono">{reviewCell(risk)}</span>
              ) : risk.vendor_note ? (
                <span className="font-serif italic">vendor: {risk.vendor_note}</span>
              ) : null}
            </span>
          </button>
        ))}
      </div>

      <div className="mt-2 flex items-baseline justify-between">
        <span className="font-serif text-scale-1 italic text-ink-muted">
          {remaining > 0 ? `${remaining} more entries — j/k to move, Enter to peek` : 'j/k to move, Enter to peek'}
        </span>
        <span className="flex items-baseline gap-3">
          <button type="button" disabled={!hasPrevious} onClick={() => props.onPageChange(page - 1)}
            className="cap-label cursor-pointer disabled:cursor-default disabled:opacity-40 hover:!text-ink">
            ← Prev
          </button>
          <span className="cap-label">Page {page} / {pageCount}</span>
          <button type="button" disabled={!hasNext} onClick={() => props.onPageChange(page + 1)}
            className="cap-label cursor-pointer disabled:cursor-default disabled:opacity-40 hover:!text-ink">
            Next →
          </button>
        </span>
      </div>
    </div>
  );
};
